import type { EnvCellSource, EnvMatrix, EnvMatrixCell, EnvMatrixIssue, EnvMatrixRow } from "./api";

/** The name of the table a viewer builds with the + chip. */
export const CUSTOM_TABLE = "Custom";

/**
 * The matrix with the viewer's own comparison added as one more table. The
 * services keep the page's order, whatever order they were ticked in, and
 * any that have since disappeared from Render drop out; with none left,
 * there's no custom table.
 */
export function withCustomTable<M extends Pick<EnvMatrix, "tables" | "services">>(
  m: M,
  picked: string[] | null,
): M {
  const serviceIds = m.services.map((s) => s.id).filter((id) => picked?.includes(id));
  if (serviceIds.length === 0) return m;
  return { ...m, tables: [...m.tables, { name: CUSTOM_TABLE, serviceIds }] };
}

/** The table a service's column sits in. */
export const tableOf = (m: Pick<EnvMatrix, "tables">, serviceId: string): string | null =>
  m.tables.find((t) => t.serviceIds.includes(serviceId))?.name ?? null;

/**
 * For each table's chip: how many issues point at its cells, and whether any
 * of them is a broken rule (red) rather than only notices (amber). Setup
 * problems belong to no table, so they're left out.
 */
export function issuesByTable(
  m: Pick<EnvMatrix, "tables" | "issues">,
): Map<string, { count: number; broken: boolean }> {
  const out = new Map(m.tables.map((t) => [t.name, { count: 0, broken: false }]));
  for (const issue of m.issues) {
    if (issue.kind === "config") continue;
    for (const t of m.tables) {
      if (!issue.cells.some((c) => t.serviceIds.includes(c.serviceId))) continue;
      const entry = out.get(t.name)!;
      entry.count += 1;
      entry.broken ||= issue.kind === "rule";
    }
  }
  return out;
}

/**
 * The rules that check `key` on any of `serviceIds`, passing or broken. A
 * rule on the portal's DATABASE_URL doesn't count for vernias-pe-dev's, so
 * the tick beside a key only ever speaks for the services on screen. Rules
 * that didn't apply (a date rule whose condition doesn't hold) are left out.
 */
export function rulesFor(
  m: Pick<EnvMatrix, "issues" | "passing">,
  key: string,
  serviceIds: string[],
): { label: string; ok: boolean }[] {
  const covers = (targets: { serviceId: string; key: string }[]) =>
    targets.some((t) => t.key === key && serviceIds.includes(t.serviceId));
  return [
    ...m.issues.filter((i) => i.kind === "rule" && covers(i.targets)).map((i) => ({ label: i.title, ok: false })),
    ...m.passing.filter((c) => c.status === "pass" && covers(c.targets)).map((c) => ({ label: c.label, ok: true })),
  ];
}

/** The rows a table shows: keys that at least one of its services has, declares or is missing. */
export function rowsForTable(rows: EnvMatrixRow[], serviceIds: string[]): EnvMatrixRow[] {
  return rows.filter((r) => serviceIds.some((id) => r.cells[id]));
}

/**
 * What two cells are compared by: the value when it's shown, otherwise the
 * server's fingerprint — never displayed, but equal exactly when the values are.
 */
const comparable = (c: EnvMatrixCell) => (c.secret ? c.fingerprint : c.value);

export interface RowComparison {
  state: "match" | "differ";
  /** Services in the table that don't have the key at all. */
  missing: string[];
}

/**
 * Whether the services in a table agree on a key — the = or ≠ beside it.
 * A service without the key counts as a different value: "set on Dev, not on
 * Demo" is a difference worth seeing. Nothing to compare (one service, or
 * nobody has a value) gives null.
 */
export function compareAcross(row: EnvMatrixRow, serviceIds: string[]): RowComparison | null {
  if (serviceIds.length < 2) return null;
  const values = serviceIds.map((id) => (row.cells[id]?.set ? comparable(row.cells[id]) : undefined));
  if (values.every((v) => v === undefined)) return null;
  return {
    state: new Set(values).size === 1 ? "match" : "differ",
    missing: serviceIds.filter((_, i) => values[i] === undefined),
  };
}

/** Keys an issue points at, including the cells it highlights. */
export function keysWithIssues(issues: EnvMatrixIssue[]): Set<string> {
  const keys = new Set<string>();
  for (const i of issues) {
    if (i.kind === "config") continue;
    i.keys.forEach((k) => keys.add(k));
    i.cells.forEach((c) => keys.add(c.key));
  }
  return keys;
}

export interface MatrixFilter {
  query: string;
  issuesOnly: boolean;
  differOnly: boolean;
}

/** `serviceIds` are the table on screen: "Values differ" compares within it. */
export function filterRows(
  rows: EnvMatrixRow[],
  f: MatrixFilter,
  issueKeys: Set<string>,
  serviceIds: string[],
): EnvMatrixRow[] {
  const q = f.query.trim().toLowerCase();
  return rows.filter(
    (r) =>
      (!q || r.key.toLowerCase().includes(q)) &&
      (!f.issuesOnly || r.status === "fail" || issueKeys.has(r.key)) &&
      (!f.differOnly || compareAcross(r, serviceIds)?.state === "differ"),
  );
}

/**
 * Secrets revealed during this page view. Each value is fetched once,
 * however often it's hovered, so the audit log gets one row per look rather
 * than one per pointer pass. Keyed by fingerprint as well, so a value that
 * changes after a refresh is fetched again. A failed fetch is forgotten, so
 * the next hover can retry.
 */
export class RevealCache {
  private readonly values = new Map<string, Promise<string>>();

  constructor(private readonly fetchValue: (serviceId: string, key: string) => Promise<string>) {}

  get(serviceId: string, key: string, fingerprint: string | null): Promise<string> {
    const id = `${serviceId}|${key}|${fingerprint ?? ""}`;
    let p = this.values.get(id);
    if (!p) {
      p = this.fetchValue(serviceId, key);
      this.values.set(id, p);
      p.catch(() => this.values.delete(id));
    }
    return p;
  }

  clear(): void {
    this.values.clear();
  }
}

export const SOURCE_LABEL: Record<EnvCellSource, string> = {
  yaml: "render.yaml (the value is in the file)",
  dashboard: "Render dashboard (render.yaml declares it with sync: false)",
  generated: "Generated by Render (generateValue in render.yaml)",
  linked: "Linked to another Render resource in render.yaml",
  group: "An env group",
  untracked: "Render dashboard only. render.yaml doesn't declare it.",
  dashboard_only: "Render dashboard only, on purpose (dashboard_only in env-rules.yaml)",
  unknown: "Unknown: render.yaml couldn't be read",
};
